/**
 * Shared template for BU + Department dashboards.
 *
 * Both endpoints return the same `EntityDashboardData` shape from the
 * backend's `buildEntityDashboard` helper. The only differences are:
 *   - the page title and "other entity" column label
 *   - which entity-row drill-through goes to the CC dashboard
 *
 * Responsive grid is consistent with the Finance + CC dashboards.
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import moment from "moment";
import { fetchGet } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import MetricTile from "./metric_tile";
import DashboardSection from "./dashboard_section";
import StatusBadge from "./status_badge";
import { EntityDashboardData } from "../models/dashboard_models";

interface Props {
  /** "bu" or "dept" — drives the endpoint path + page label */
  variant: "bu" | "dept";
}

const formatCurrency = (n: number) =>
  "₹" + (Number(n) || 0).toLocaleString("en-IN");
const formatShort = (n: number) => {
  const v = Math.abs(n);
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(1) + "Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(1) + "L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n);
};

const EntityDashboard: FunctionComponent<Props> = ({ variant }) => {
  const { id } = useParams<{ id: string }>();
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EntityDashboardData | null>(null);

  const label = variant === "bu" ? "Business Unit" : "Department";
  const otherLabel = variant === "bu" ? "Department" : "Business Unit";
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/dashboard/${variant}/${id}`;

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchGet(url);
      if (res.success) setData(res.data);
      else showToast({ type: ToastType.error, text: res.error });
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const utilization = useMemo(() => {
    if (!data) return 0;
    const est = data.kpis.mtd_estimate || 0;
    if (est <= 0) return 0;
    return (data.kpis.mtd_actual / est) * 100;
  }, [data]);

  const trendOptions = useMemo(
    () =>
      ({
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "Nunito, sans-serif",
        },
        colors: ["#94a3b8", "#00BEA5"],
        stroke: { curve: "smooth", width: [2, 3] },
        dataLabels: { enabled: false },
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: {
          categories: (data?.last_12_months || []).map((m) =>
            moment(m.month + "-01").format("MMM YY"),
          ),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            formatter: (v: number) => formatShort(v),
            style: { fontSize: "11px" },
          },
        },
        tooltip: { y: { formatter: (v: number) => formatCurrency(v) } },
        grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      }) as any,
    [data],
  );

  const trendSeries = useMemo(() => {
    if (!data) return [];
    return [
      {
        name: "Estimate",
        data: data.last_12_months.map((m) => Math.round(m.estimate)),
      },
      {
        name: "Actual",
        data: data.last_12_months.map((m) => Math.round(m.actual)),
      },
    ];
  }, [data]);

  // CC-by-CC bar chart
  const ccBarOptions = useMemo(
    () =>
      ({
        chart: {
          toolbar: { show: false },
          fontFamily: "Nunito, sans-serif",
        },
        colors: ["#94a3b8", "#00BEA5"],
        plotOptions: {
          bar: { columnWidth: "55%", borderRadius: 4 },
        },
        dataLabels: { enabled: false },
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: {
          categories: (data?.cc_breakdown || [])
            .slice(0, 10)
            .map((c) => c.name),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            formatter: (v: number) => formatShort(v),
            style: { fontSize: "11px" },
          },
        },
        tooltip: { y: { formatter: (v: number) => formatCurrency(v) } },
        grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      }) as any,
    [data],
  );

  const ccBarSeries = useMemo(() => {
    if (!data) return [];
    const top = data.cc_breakdown.slice(0, 10);
    return [
      {
        name: "Estimate",
        data: top.map((c) => Math.round(c.monthly_estimate)),
      },
      { name: "Actual", data: top.map((c) => Math.round(c.mtd_actual)) },
    ];
  }, [data]);

  if (loading && !data) return <LoadingWidget />;
  if (!data)
    return (
      <div className="p-5 text-sm text-gray-500">
        No dashboard data available.
      </div>
    );

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <button
            onClick={() => navigation.goBack()}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            &larr; Back
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            {data.entity.name}{" "}
            <span className="text-sm text-gray-400 font-normal">
              ({label})
            </span>
          </h1>
          <p className="text-xs text-gray-500">
            <span className="font-mono">{data.entity.id}</span>{" "}
            <span className="text-gray-300">·</span>{" "}
            FY <b>{data.entity.current_fy || data.fy}</b>{" "}
            <span className="text-gray-300">·</span> Month{" "}
            <b>{data.month}</b>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={data.entity.status} />
          <button
            onClick={load}
            className="text-xs border rounded-md px-3 py-1 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          title="Annual Plan"
          value={formatShort(data.kpis.annual_estimate)}
          subtitle={`${data.kpis.cc_count} CC${
            data.kpis.cc_count === 1 ? "" : "s"
          } in scope`}
          accent="primary"
        />
        <MetricTile
          title="MTD Actual"
          value={formatShort(data.kpis.mtd_actual)}
          subtitle={`vs slice ${formatShort(data.kpis.mtd_estimate)}`}
          accent={utilization > 100 ? "rose" : "blue"}
          trend={
            data.kpis.mtd_estimate > 0
              ? {
                  value: utilization - 100,
                  suffix: "% vs est",
                  invertColors: true,
                }
              : undefined
          }
        />
        <MetricTile
          title="YTD Actual"
          value={formatShort(data.kpis.ytd_actual)}
          subtitle="Finance-approved since Apr"
          accent="neutral"
        />
        <MetricTile
          title="Projects this month"
          value={data.kpis.projects_this_month}
          subtitle="Finance-approved"
          accent={
            data.kpis.balance_this_month < 0 ? "rose" : "green"
          }
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="12-month trend"
          subtitle="Slice estimate vs approved actuals"
        >
          <Chart
            options={trendOptions}
            series={trendSeries}
            type="area"
            height={280}
          />
        </DashboardSection>

        <DashboardSection
          title="MTD by Cost Center"
          subtitle={
            data.cc_breakdown.length === 0
              ? "No CCs in scope"
              : `Top ${Math.min(data.cc_breakdown.length, 10)} of ${
                  data.cc_breakdown.length
                }`
          }
        >
          {data.cc_breakdown.length === 0 ? (
            <p className="text-xs text-gray-400 py-10 text-center">
              No Cost Centers under this {label} yet.
            </p>
          ) : (
            <Chart
              options={ccBarOptions}
              series={ccBarSeries}
              type="bar"
              height={280}
            />
          )}
        </DashboardSection>
      </div>

      {/* CC breakdown table */}
      <DashboardSection
        title="Cost Centers"
        subtitle="Sorted by MTD overrun (worst first)"
      >
        {data.cc_breakdown.length === 0 ? (
          <p className="text-xs text-gray-400 py-3 text-center">
            No Cost Centers under this {label}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left font-medium py-1.5">Name</th>
                  <th className="text-left font-medium py-1.5">
                    {otherLabel}
                  </th>
                  <th className="text-right font-medium py-1.5">Estimate</th>
                  <th className="text-right font-medium py-1.5">MTD Actual</th>
                  <th className="text-right font-medium py-1.5">Over/Under</th>
                  <th className="text-left font-medium py-1.5">Renewal</th>
                </tr>
              </thead>
              <tbody>
                {data.cc_breakdown.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t cursor-pointer hover:bg-gray-50"
                    onClick={() =>
                      navigation.push(`${urls.cc_dashboard}/${c.id}`)
                    }
                  >
                    <td className="py-2 pr-2">
                      <div className="font-medium text-gray-800">{c.name}</div>
                      <div className="text-gray-400 font-mono text-[10px]">
                        {c.id}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-gray-600">
                      {c.other_name || "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatShort(c.monthly_estimate)}
                    </td>
                    <td
                      className={`py-2 text-right tabular-nums ${
                        c.mtd_actual > c.monthly_estimate
                          ? "text-rose-700"
                          : ""
                      }`}
                    >
                      {formatShort(c.mtd_actual)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <span
                        className={
                          c.overrun_pct > 0
                            ? "text-rose-700 font-semibold"
                            : c.overrun_pct < 0
                              ? "text-blue-700"
                              : "text-gray-500"
                        }
                      >
                        {c.overrun_pct > 0 ? "+" : ""}
                        {c.overrun_pct.toFixed(0)}%
                      </span>
                    </td>
                    <td className="py-2 pl-2">
                      <StatusBadge status={c.current_month_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DashboardSection>

      {/* Pending approvals + recalibration history side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="Pending approvals"
          subtitle={
            data.pending_approvals.length === 0
              ? "Nothing waiting"
              : `${data.pending_approvals.length} project(s) in this ${label}`
          }
        >
          {data.pending_approvals.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              All caught up.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">Project</th>
                    <th className="text-right font-medium py-1.5">Budget</th>
                    <th className="text-left font-medium py-1.5">Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending_approvals.map((p: any) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-800">
                          {p.name}
                        </div>
                        <div className="text-gray-400 text-[10px] font-mono">
                          {p.id}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(p.budget)}
                      </td>
                      <td className="py-2 pl-2">
                        <StatusBadge status={p.current_approval_level} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>

        <DashboardSection
          title="Recent recalibrations"
          subtitle="Last 6 month-end adjustments to annual plan"
        >
          {data.entity.recalibration_history.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              No recalibrations recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">Month</th>
                    <th className="text-right font-medium py-1.5">Actual</th>
                    <th className="text-right font-medium py-1.5">Delta</th>
                    <th className="text-right font-medium py-1.5">New Annual</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entity.recalibration_history.map((r, idx) => (
                    <tr key={r.month + idx} className="border-t">
                      <td className="py-2 pr-2 font-medium text-gray-800">
                        {r.month}
                        <div className="text-[10px] text-gray-400">
                          {r.reason}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(r.prior_month_actual)}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums font-semibold ${
                          r.delta > 0
                            ? "text-rose-700"
                            : r.delta < 0
                              ? "text-blue-700"
                              : "text-gray-500"
                        }`}
                      >
                        {r.delta > 0 ? "+" : ""}
                        {formatShort(r.delta)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(r.new_annual)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default EntityDashboard;
